import React, { useEffect, useRef,useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import ZpButton from 'components/zpButton';
import { freezeBodyScroll, importStatic,unFreezeBodyScroll } from 'utils';

import { STATE_HISTORY_OPERATIONS } from '../../store/searchFilter';
import { updateFiltersState } from '../../store/searchFilter/actions';

import { mapFilterValues } from './filterUtils';
import ZpFilter from './index';

import zpFilterClasses from './zpFilter.module.css';

const propTypes = {
  filterGroup: PropTypes.string.isRequired,
  filters: PropTypes.arrayOf(PropTypes.object).isRequired,
  onApply: PropTypes.func,
  compact: PropTypes.bool,
  singleCompactFilter: PropTypes.bool,
};

const defaultProps = {
  filterGroup: '',
  filters: [],
  onApply: () => {},
  compact: true,
  singleCompactFilter: false,
};

const FilterCarousel = ({
  compact,
  filters,
  onApply,
  filterGroup,
  singleCompactFilter,
  containerClass = '',
}) => {
  const ref = useRef();

  const dispatch = useDispatch();

  const { initialFiltersOpen, initialFiltersValue } = setInitialFilters();

  const [compactFilters, setCompactFilters] = useState(filters);

  const [filtersOpen, setFiltersOpen] = useState(initialFiltersOpen);

  const [filtersValue, setFiltersValue] = useState(initialFiltersValue);

  const [isCompact, setCompact] = useState(false);

  const [filtersActive, setFiltersActive] = useState(isFiltersActive(filtersValue));

  const stateHistory = useSelector(
    ({ searchFilter }) => searchFilter.stateHistory[filterGroup] || []
  );

  const stateHistoryLength = useSelector(
    ({ searchFilter }) => (searchFilter.stateHistory[filterGroup] || []).length
  );

  const stateHistoryChanged = useSelector(({ searchFilter }) => searchFilter.stateHistoryChanged);

  const jobsByCompany = useSelector(({ jobs }) => jobs.jobsByCompany);

  useEffect(() => {
    ref.current = filters;
    updateStateHistory(filtersValue, STATE_HISTORY_OPERATIONS.INIT);
  }, [filterGroup]);

  useEffect(() => {
    if (stateHistoryChanged) {
      onApply(mapFilterValues(stateHistory[stateHistoryLength - 1]));
      setFiltersValue(stateHistory[stateHistoryLength - 1]);
      setFiltersActive(isFiltersActive(stateHistory[stateHistoryLength - 1]));
    }
  }, [stateHistoryLength]);

  useEffect(() => {
    const { initialFiltersValue } = setInitialFilters();
    setFiltersActive(isFiltersActive(initialFiltersValue));
  }, []);

  useEffect(() => {
    if (!_isEqual(compactFilters, filters)) {
      setCompactFilters(filters);
    }
  }, [filters]);

  function updateStateHistory(newState, operation) {
    dispatch(updateFiltersState({ filterGroup, newState, operation }));
  }

  useEffect(() => {
    if (
      filtersValue.location?.length &&
      jobsByCompany &&
      jobsByCompany.totalJobs === 0 &&
      jobsByCompany.pageNo === 0
    ) {
      filtersValue.location = [];
      updateStateHistory(filtersValue, STATE_HISTORY_OPERATIONS.APPEND);
    }
  }, [jobsByCompany]);

  function setInitialFilters() {
    const initialFiltersOpen = {};
    const initialFiltersValue = {};

    filters.forEach(({ param, valueRange = [], preselectedItems = [] }) => {
      initialFiltersOpen[param] = false;
      const allValues = [
        ...valueRange,
        ...preselectedItems.map((item) => ({ ...item, enabled: true })),
      ];
      const enabledValues = allValues.filter(({ enabled }) => enabled);
      initialFiltersValue[param] = enabledValues;
    });
    return { initialFiltersOpen, initialFiltersValue };
  }

  function isFiltersActive(filtersValue) {
    if (!filtersValue) return false;
    for (const param of Object.keys(filtersValue).slice(singleCompactFilter ? 0 : 1)) {
      if (filtersValue[param] && filtersValue[param].length) {
        return true;
      }
    }
    return false;
  }

  const handleFilterClick = (param) => {
    const filtersOpenCopy = { ...filtersOpen };
    for (const currentParam in filtersOpenCopy) {
      if (currentParam === param) {
        filtersOpenCopy[currentParam] = !filtersOpenCopy[param];
      } else {
        filtersOpenCopy[currentParam] = false;
      }
    }
    setFiltersOpen(filtersOpenCopy);
  };

  const handleFilterApply = (param, value) => {
    const filtersValueCopy = { ...filtersValue };
    filtersValueCopy[param] = !Array.isArray(value) ? [value] : value;
    updateStateHistory(filtersValueCopy, STATE_HISTORY_OPERATIONS.APPEND);
  };

  const zIndexUpdate = (add = true) => {
    const header = document.querySelector('header.header.header-new');
    const navigation = document.querySelector('#navigation');
    if (header) {
      if (add) {
        header.style.zIndex = 100;
      } else {
        header.style.zIndex = 99999;
      }
    }
    if (navigation) {
      if (add) {
        navigation.style.zIndex = 100;
      } else {
        navigation.style.zIndex = 99998;
      }
    }
  };

  const handleCompactClick = (isCompact) => {
    const filtersOpenCopy = { ...filtersOpen };
    Object.keys(filtersOpenCopy)
      .slice(singleCompactFilter ? 0 : 1)
      .forEach((param) => {
        filtersOpenCopy[param] = !filtersOpenCopy[param];
      });
    setFiltersOpen(filtersOpenCopy);
    setCompact(isCompact);
    if (isCompact) {
      zIndexUpdate();
      freezeBodyScroll();
    } else {
      zIndexUpdate(false);
      unFreezeBodyScroll();
    }
  };

  const handleCompactFilterSelect = (param, value) => {
    const filtersValueCopy = { ...filtersValue };
    filtersValueCopy[param] = !Array.isArray(value) ? [value] : value;
    setFiltersValue(filtersValueCopy);
  };

  const handleCompactApply = () => {
    updateStateHistory(filtersValue, STATE_HISTORY_OPERATIONS.APPEND);
    let compactFiltersCopy = compactFilters.map((filter) => ({
      ...filter,
      preselectedItems: filtersValue?.[filter.param] || [],
    }));
    setCompactFilters(compactFiltersCopy);
    handleCompactClick(false);
    zIndexUpdate(false);
    unFreezeBodyScroll();
  };

  const handleCompactClear = () => {
    const filtersValueCopy = { ...filtersValue };
    let compactFiltersCopy = [...compactFilters];
    Object.keys(filtersValueCopy)
      .slice(singleCompactFilter ? 0 : 1)
      .forEach((param) => {
        filtersValueCopy[param] = [];
      });
    compactFiltersCopy = compactFiltersCopy.map((filter) => ({
      ...filter,
      preselectedItems: [],
    }));
    setCompactFilters(compactFiltersCopy);
    updateStateHistory(filtersValueCopy, STATE_HISTORY_OPERATIONS.APPEND);
    handleCompactClick(false);
    zIndexUpdate(false);
    unFreezeBodyScroll();
  };

  return (
    <>
      <div
        className={classNames(
          zpFilterClasses['zp-filter-group'],
          'zp-filter-group',
          containerClass,
          'p-0'
        )}
      >
        <div
          className={classNames(
            zpFilterClasses['filter-carousel-container'],
            'filter-carousel-container'
          )}
        >
          <div
            className={classNames(
              zpFilterClasses['filter-carousel'],
              `${!compact ? 'd-flex' : 'd-none'} d-lg-flex `
            )}
          >
            {filters.map((filter, idx) => (
              <ZpFilter
                key={idx}
                alignRight={idx > 3}
                {...filter}
                filterGroup={filterGroup}
                filterOpen={filtersOpen[filter.param]}
                onFilterClick={handleFilterClick}
                onApply={handleFilterApply}
                onClear={handleFilterApply}
                filterEnabledValues={
                  stateHistory.length && stateHistory[stateHistory.length - 1]
                    ? stateHistory[stateHistory.length - 1][filter.param]
                    : []
                }
              />
            ))}
          </div>
          <div className={`${!compact ? 'd-none' : 'd-flex'} d-lg-none`}>
            {!singleCompactFilter &&
              filters
                .slice(0, filters.length < 3 ? 3 : 1)
                .map((filter, idx) => (
                  <ZpFilter
                    key={idx}
                    {...filter}
                    filterGroup={filterGroup}
                    filterOpen={filtersOpen[filter.param]}
                    onFilterClick={handleFilterClick}
                    onApply={handleFilterApply}
                    onClear={handleFilterApply}
                    filterEnabledValues={
                      stateHistory.length && stateHistory[stateHistory.length - 1]
                        ? stateHistory[stateHistory.length - 1][filter.param]
                        : []
                    }
                  />
                ))}

            {filters.length > 2 && (
              <>
                <ZpButton
                  theme={filtersActive ? 'primary' : 'secondary'}
                  onClick={() => handleCompactClick(true)}
                  dropdownStatus="closed"
                  isLeftIcon={false}
                >
                  Filters
                </ZpButton>
                {isCompact && (
                  <div
                    className={classNames(
                      zpFilterClasses['compact-filter-wrapper'],
                      ' d-flex flex-column'
                    )}
                  >
                    <div
                      className={classNames(
                        zpFilterClasses['filter-toolbar'],
                        'd-flex justify-content-between align-items-center d-lg-none filter-toolbar'
                      )}
                    >
                      <div className="filter-toolbar-start">
                        <img
                          src={importStatic('/ui-router/images/select-x-dark.svg')}
                          alt="cross-icon"
                          onClick={() => handleCompactClick(false)}
                        />
                      </div>
                      <div className="filter-toolbar-end" onClick={() => handleCompactClear()}>
                        Clear
                      </div>
                    </div>

                    <div className={zpFilterClasses['compact-filters']}>
                      {compactFilters.slice(singleCompactFilter ? 0 : 1).map((filter, idx) => (
                        <ZpFilter
                          key={idx}
                          {...filter}
                          filterGroup={filterGroup}
                          isCompact={isCompact}
                          filterOpen={filtersOpen[filter.param]}
                          filterEnabledValues={filtersValue[filter.param]}
                          onFilterSelect={handleCompactFilterSelect}
                        />
                      ))}
                    </div>

                    <div className={zpFilterClasses['filter-footer']}>
                      <div
                        className={zpFilterClasses['filter-footer-end']}
                        onClick={() => handleCompactApply()}
                      >
                        Apply
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

FilterCarousel.propTypes = propTypes;
FilterCarousel.defaultProps = defaultProps;

export default React.memo(FilterCarousel);
